/**
 * "Estou na espera" — do lado de quem espera.
 *
 * Serve para duas coisas, e as duas importam: lembrar o cliente do que ele
 * pediu (senão ele acha que a barbearia esqueceu) e dar a ele um jeito de sair
 * sozinho. Sem a saída, o barbeiro vira secretário de gente que já resolveu a
 * vida em outro lugar.
 *
 * Note que a espera convive com o horário marcado logo acima dela. É de
 * propósito: o caso real é justamente marcar o horário ruim que sobrou **e**
 * continuar esperando um melhor.
 */

import type { ServiceView, WaitlistEntryView } from '@barbearia/shared';
import { describeServices, MAX_WAITLIST_PER_CLIENT } from '@barbearia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, ErrorNote, Field, OptionList, Sheet } from '../../components/ui.tsx';
import { useWaitlistWindow } from '../../components/WaitlistWindow.tsx';
import { ApiError, api } from '../../lib/api.ts';
import { duration } from '../../lib/format.ts';

export function MyWaitlist() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const { data } = useQuery({
    queryKey: ['my-waitlist'],
    queryFn: () => api.get<{ entries: WaitlistEntryView[] }>('/waitlist/mine'),
  });

  const leave = useMutation({
    mutationFn: (id: string) => api.delete(`/waitlist/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['my-waitlist'] }),
  });

  const entries = data?.entries ?? [];
  const full = entries.length >= MAX_WAITLIST_PER_CLIENT;

  return (
    <section className="px-4 pt-6">
      <h2 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
        Aguardando vaga
      </h2>

      {entries.length === 0 ? (
        <div className="rounded-xl border border-line bg-surface-raised px-4 py-4">
          <p className="text-[14px] text-ink-soft">
            Não achou horário bom? Peça para ser avisado quando abrir um que sirva.
          </p>
          <Button variant="secondary" full className="mt-3" onClick={() => setCreating(true)}>
            Avisar quando vagar
          </Button>
        </div>
      ) : (
        <>
          <ul className="overflow-hidden rounded-xl border border-line">
            {entries.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center gap-3 border-b border-line bg-surface-raised px-4 py-3 last:border-b-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[15px] text-ink">{entry.summary}</span>
                  <span className="block text-[12px] text-ink-faint">
                    {describeServices(entry.services.map((service) => service.name))} ·{' '}
                    {duration(entry.durationMinutes)}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => leave.mutate(entry.id)}
                  disabled={leave.isPending}
                  className="shrink-0 touch-target px-2 text-[13px] text-ink-faint"
                >
                  Sair
                </button>
              </li>
            ))}
          </ul>

          {!full && (
            <button
              type="button"
              onClick={() => setCreating(true)}
              className="touch-target px-1 text-[13px] text-accent"
            >
              + Esperar por outro horário
            </button>
          )}
        </>
      )}

      <p className="px-1 pt-2 text-[12px] text-ink-faint">
        A barbearia chama você pelo WhatsApp. Ter horário marcado não tira você da espera.
      </p>

      {creating && <JoinWaitlistSheet onClose={() => setCreating(false)} />}
    </section>
  );
}

/**
 * Entrar na fila.
 *
 * Aceita serviço e profissional já escolhidos porque a folha também abre da
 * tela de Agendar, onde a pessoa acabou de escolher os dois. Perguntar de novo
 * ali seria fazê-la refazer o que já fez — e a diferença entre dois toques e
 * seis é a diferença entre usar e desistir.
 */
export function JoinWaitlistSheet({
  onClose,
  defaultServiceIds,
  membershipId,
}: {
  onClose: () => void;
  defaultServiceIds?: readonly string[];
  membershipId?: string | null;
}) {
  const queryClient = useQueryClient();

  // `/services` é da equipe. O cliente lê o catálogo por `/services/available`,
  // que devolve o que está ativo sem depender do ajuste de vitrine.
  const { data } = useQuery({
    queryKey: ['services-available'],
    queryFn: () => api.get<{ services: ServiceView[] }>('/services/available'),
  });

  const services = data?.services ?? [];

  const [serviceIds, setServiceIds] = useState<string[]>([...(defaultServiceIds ?? [])]);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { value: window, picker } = useWaitlistWindow();

  const join = useMutation({
    mutationFn: () =>
      api.post('/waitlist/request', {
        serviceIds,
        membershipId: membershipId ?? undefined,
        note: note.trim() || undefined,
        ...window,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-waitlist'] });
      onClose();
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : 'Não deu certo'),
  });

  return (
    <Sheet
      open
      onClose={onClose}
      title="Avisar quando vagar"
      footer={
        <Button
          full
          disabled={serviceIds.length === 0}
          loading={join.isPending}
          onClick={() => join.mutate()}
        >
          {serviceIds.length > 0 ? 'Entrar na espera' : 'Escolha o serviço'}
        </Button>
      }
    >
      <div className="flex flex-col gap-5">
        <section>
          <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            O que você quer fazer
          </h3>
          <OptionList
            items={services}
            selected={serviceIds}
            onSelect={(service) =>
              setServiceIds((current) =>
                current.includes(service.id)
                  ? current.filter((id) => id !== service.id)
                  : [...current, service.id],
              )
            }
            getKey={(service) => service.id}
            renderTitle={(service) => service.name}
            renderMeta={(service) => duration(service.defaultDurationMinutes)}
          />
        </section>

        <section>
          <h3 className="pb-2 text-[13px] font-semibold tracking-wide text-ink-faint uppercase">
            Quando serve para você
          </h3>
          {picker}
        </section>

        {/*
          Nem toda preferência cabe numa faixa de horário. "Só se for com o Zé",
          "não pode ser dia de jogo", "prefiro logo depois do almoço" — é o
          barbeiro que lê isso, e é ele que sabe o que fazer com a informação.
        */}
        <Field
          label="Quer dizer mais alguma coisa? (opcional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Ex.: só depois do trabalho, prefiro sexta"
          maxLength={300}
        />

        {error && <ErrorNote>{error}</ErrorNote>}

        <p className="text-[13px] text-ink-faint">
          Vale por 30 dias. A barbearia chama pelo WhatsApp quando abrir algo — o horário não fica
          reservado até você responder.
        </p>
      </div>
    </Sheet>
  );
}
